import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionHeading, MuhuratCard, StatsSection, TestimonialsSection } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import {
  Sunrise, Sunset, Moon, Sun, AlertTriangle, Sparkles, ArrowRight,
  CalendarDays, Compass, Star, CalendarRange, ScrollText, BookOpen, MapPin,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WD_IDX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const CITIES = [
  { name: "Varanasi", coord: "25.3176,82.9739", offset: "+05:30" },
  { name: "New Delhi", coord: "28.6139,77.2090", offset: "+05:30" },
  { name: "Mumbai", coord: "19.0760,72.8777", offset: "+05:30" },
  { name: "Kolkata", coord: "22.5726,88.3639", offset: "+05:30" },
  { name: "Chennai", coord: "13.0827,80.2707", offset: "+05:30" },
  { name: "Bengaluru", coord: "12.9716,77.5946", offset: "+05:30" },
  { name: "Ujjain", coord: "23.1765,75.7885", offset: "+05:30" },
  { name: "Ahmedabad", coord: "23.0225,72.5714", offset: "+05:30" },
  { name: "Jaipur", coord: "26.9124,75.7873", offset: "+05:30" },
  { name: "Pune", coord: "18.5204,73.8567", offset: "+05:30" },
];

const LANGS = [
  { code: "", name: "English" },
  { code: "hi", name: "हिन्दी" },
  { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" },
  { code: "bn", name: "বাংলা" },
  { code: "mr", name: "मराठी" },
  { code: "gu", name: "ગુજરાતી" },
  { code: "kn", name: "ಕನ್ನಡ" },
];

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-secondary/60 border border-border/60 p-4">
    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="font-serif text-base font-semibold mt-1 leading-tight">{value}</p>
  </div>
);

const IconStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-secondary/60 border border-border/60 p-4 flex items-center gap-2.5">
    <Icon className="h-5 w-5 text-accent shrink-0" />
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-semibold text-sm mt-0.5">{value}</p>
    </div>
  </div>
);

const CHO_COLOR = (t) => (t === "Inauspicious" ? "text-destructive" : t === "Good" ? "text-amber-600" : "text-emerald-600");
const ChoghadiyaCard = ({ title, items, icon: Icon }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={`choghadiya-${title.split(" ")[0].toLowerCase()}`}>
    <p className="flex items-center gap-2 font-serif text-lg font-semibold mb-3"><Icon className="h-4 w-4 text-accent" /> {title}</p>
    <div className="space-y-1.5">
      {items.map((c, i) => (
        <div key={i} className="flex items-center justify-between text-sm border-b border-border/40 pb-1.5 last:border-0">
          <span className="font-medium">{c.name} <span className={`text-xs ${CHO_COLOR(c.type)}`}>· {c.type}</span></span>
          <span className="text-muted-foreground">{c.time}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function Panchang() {
  const [p, setP] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [rituals, setRituals] = useState([]);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [showMonth, setShowMonth] = useState(false);
  const [month, setMonth] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [loc, setLoc] = useState(CITIES[0]);
  const [lang, setLang] = useState("");
  const [muhurat, setMuhurat] = useState(null);
  const [searchParams] = useSearchParams();
  const monthRef = useRef(null);

  useEffect(() => {
    api.get("/festivals").then((r) => setFestivals(r.data));
    api.get("/vedic-rituals").then((r) => setRituals(r.data));
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/blogs").then((r) => setBlogs(r.data));
  }, []);

  // Auto-open the monthly calendar when arrived via ?view=month
  useEffect(() => {
    if (searchParams.get("view") === "month") {
      setShowMonth(true);
      setTimeout(() => monthRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [searchParams]);

  useEffect(() => {
    setP(null);
    setMuhurat(null);
    const params = { coordinates: loc.coord, offset: loc.offset, lang: lang || undefined };
    api.get("/panchang/today", { params }).then((r) => setP(r.data));
    api.get("/panchang/muhurat", { params }).then((r) => setMuhurat(r.data)).catch(() => {});
  }, [loc, lang]);

  // Fetch the monthly calendar whenever it is open / month or location changes
  useEffect(() => {
    if (!showMonth) return;
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    setMonth(null);
    api.get("/panchang/month", {
      params: { year: target.getFullYear(), month: target.getMonth() + 1, coordinates: loc.coord, offset: loc.offset, lang: lang || undefined },
    }).then((r) => setMonth(r.data));
  }, [showMonth, monthOffset, loc, lang]);

  const changeCity = (name) => {
    setLoc(CITIES.find((x) => x.name === name) || CITIES[0]);
  };

  const openMonth = () => setShowMonth((s) => !s);

  const panchangBlogs = blogs.filter((b) => b.category === "Astrology").slice(0, 3);
  const guide = blogs.find((b) => b.slug === "understanding-panchang");

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 lg:py-16" data-testid="panchang-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">Vedic Panchang</p>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold">Today's Hindu Panchang</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin className="h-4 w-4 text-accent" />
          <select data-testid="panchang-city-select" value={loc.name} onChange={(e) => changeCity(e.target.value)}
            className="h-10 rounded-full border border-border bg-card px-4 text-sm">
            {CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <select data-testid="panchang-lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
            className="h-10 rounded-full border border-border bg-card px-4 text-sm">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </div>
      </div>

      {/* Full-section quick-stack Panchang */}
      {p && (
        <section data-testid="daily-panchang-section" className="rounded-2xl bg-card/95 border border-accent/40 shadow-sm p-6 lg:p-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-accent/10 blur-3xl" />
          <div className="flex items-center justify-between mb-6 relative">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Today's Panchang</p>
              <p className="font-serif text-xl font-semibold mt-0.5">{p.date}</p>
            </div>
            <span className="font-devanagari text-4xl text-accent">ॐ</span>
          </div>

          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2.5">Panchang Elements</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Vaar" value={p.vaar} testid="panchang-vaar" />
            <Stat label="Tithi" value={p.tithi} testid="panchang-tithi" />
            <Stat label="Paksha" value={p.paksha} />
            <Stat label="Nakshatra" value={p.nakshatra} />
            <Stat label="Yoga" value={p.yoga} />
            <Stat label="Karana" value={p.karana} />
          </div>

          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2.5 mt-6">Sun & Moon</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <IconStat icon={Sunrise} label="Sunrise" value={p.sunrise} />
            <IconStat icon={Sunset} label="Sunset" value={p.sunset} />
            <IconStat icon={Moon} label="Moonrise" value={p.moonrise} />
            <IconStat icon={Sun} label="Moonset" value={p.moonset} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-3"><Sparkles className="h-4 w-4" /> Auspicious Timings</p>
              {p.auspicious.map((a) => (
                <p key={a.name} className="text-sm flex justify-between py-1 border-b border-border/40 last:border-0"><span className="text-muted-foreground">{a.name}</span><span className="font-medium">{a.time}</span></p>
              ))}
            </div>
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-destructive uppercase tracking-wide mb-3"><AlertTriangle className="h-4 w-4" /> Inauspicious Timings</p>
              {p.inauspicious.map((a) => (
                <p key={a.name} className="text-sm flex justify-between py-1 border-b border-border/40 last:border-0"><span className="text-muted-foreground">{a.name}</span><span className="font-medium">{a.time}</span></p>
              ))}
            </div>
          </div>

          <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2.5 mt-6">More Details</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <IconStat icon={Compass} label="Ritu (Season)" value={p.ritu} />
            <IconStat icon={Moon} label="Moon Sign" value={p.moon_sign} />
            <IconStat icon={Star} label="Sun Sign" value={p.sun_sign} />
          </div>
        </section>
      )}

      {/* Choghadiya & Auspicious Timings (live) */}
      {muhurat && muhurat.choghadiya_day.length > 0 && (
        <section className="mt-14" data-testid="choghadiya-section">
          <SectionHeading eyebrow="Muhurat" title="Today's Choghadiya & Auspicious Timings"
            subtitle="Live day and night Choghadiya with Abhijit Muhurat, Amrit Kaal and Brahma Muhurat for your chosen city." />
          <div className="grid md:grid-cols-2 gap-5">
            <ChoghadiyaCard title="Day Choghadiya" items={muhurat.choghadiya_day} icon={Sun} />
            <ChoghadiyaCard title="Night Choghadiya" items={muhurat.choghadiya_night} icon={Moon} />
          </div>
          {muhurat.auspicious.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-3"><Sparkles className="h-4 w-4" /> Auspicious Muhurat</p>
                {muhurat.auspicious.map((a) => (<p key={a.name} className="text-sm flex justify-between py-1 border-b border-border/40 last:border-0"><span className="text-muted-foreground">{a.name}</span><span className="font-medium">{a.time}</span></p>))}
              </div>
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-destructive uppercase tracking-wide mb-3"><AlertTriangle className="h-4 w-4" /> Inauspicious Periods</p>
                {muhurat.inauspicious.map((a) => (<p key={a.name} className="text-sm flex justify-between py-1 border-b border-border/40 last:border-0"><span className="text-muted-foreground">{a.name}</span><span className="font-medium">{a.time}</span></p>))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Monthly Panchang — Calendar */}
      <section className="mt-12 scroll-mt-24" ref={monthRef} data-testid="monthly-panchang-section">
        <div className="rounded-2xl border border-accent/40 bg-card/90 shadow-sm p-6 lg:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold flex items-center gap-1.5"><CalendarRange className="h-4 w-4" /> Monthly View</p>
              <h2 className="font-serif text-2xl font-semibold mt-1.5">Full Vedic Panchang{month ? ` — ${month.month_name} ${month.year}` : ""}</h2>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-xl">Tithi, Nakshatra, sunrise/sunset and special days for the whole month in a calendar view.</p>
            </div>
            <Button onClick={openMonth} data-testid="toggle-monthly-panchang" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0">
              {showMonth ? "Hide monthly view" : "View Full Vedic Panchang (Monthly)"}
            </Button>
          </div>

          {showMonth && (
            <div className="mt-6 flex items-center justify-center gap-4" data-testid="month-nav">
              <Button variant="outline" size="icon" onClick={() => setMonthOffset((o) => o - 1)} data-testid="month-prev" className="rounded-full h-9 w-9 border-border"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="font-serif text-lg font-semibold min-w-[180px] text-center" data-testid="month-label">{(() => { const n = new Date(); const t = new Date(n.getFullYear(), n.getMonth() + monthOffset, 1); return t.toLocaleString("en-US", { month: "long", year: "numeric" }); })()}</span>
              <Button variant="outline" size="icon" onClick={() => setMonthOffset((o) => o + 1)} data-testid="month-next" className="rounded-full h-9 w-9 border-border"><ChevronRight className="h-4 w-4" /></Button>
              {monthOffset !== 0 && <Button variant="ghost" onClick={() => setMonthOffset(0)} data-testid="month-today" className="rounded-full h-9 px-4 text-sm text-primary">This month</Button>}
            </div>
          )}

          {showMonth && !month && <p className="text-sm text-muted-foreground mt-6 text-center" data-testid="month-loading">Loading monthly Panchang…</p>}

          {showMonth && month && (
            <div className="mt-6 overflow-x-auto" data-testid="monthly-panchang-calendar">
              <div className="min-w-[760px]">
                <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                  {WEEKDAYS.map((w) => (
                    <div key={w} className="text-center text-[11px] uppercase tracking-widest font-semibold text-muted-foreground py-2">{w}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {Array.from({ length: WD_IDX[month.days[0].weekday] ?? 0 }).map((_, i) => (
                    <div key={`blank-${i}`} className="rounded-xl bg-secondary/20 border border-transparent min-h-[112px]" />
                  ))}
                  {month.days.map((d) => (
                    <div key={d.day} data-testid={`month-day-${d.day}`}
                      className={`rounded-xl border p-2.5 min-h-[112px] flex flex-col transition-colors hover:border-accent ${d.special ? "border-accent/50 bg-accent/5" : "border-border bg-card"}`}>
                      <div className="flex items-start justify-between">
                        <span className="font-serif text-base font-bold leading-none">{d.day}</span>
                        {d.special && <span className="text-accent shrink-0"><Sparkles className="h-3.5 w-3.5" /></span>}
                      </div>
                      <p className="text-[11px] font-medium mt-1.5 leading-tight">{d.tithi}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{d.nakshatra}</p>
                      <div className="mt-auto pt-1.5 flex items-center gap-2 text-[9px] text-muted-foreground">
                        <span className="inline-flex items-center gap-0.5"><Sunrise className="h-2.5 w-2.5 text-amber-500" />{d.sunrise}</span>
                        <span className="inline-flex items-center gap-0.5"><Sunset className="h-2.5 w-2.5 text-orange-500" />{d.sunset}</span>
                      </div>
                      {d.special && <p className="text-[9px] text-accent font-semibold mt-1 leading-tight truncate" title={d.special}>{d.special}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3">Live monthly Panchang via Prokerala. Days fill progressively (rate-limited free tier) and are pre-warmed nightly; any not-yet-fetched day shows a sample value until cached.</p>
        </div>
      </section>

      {/* Muhurat — split into Festivals & Vrat, and Vedic Rituals */}
      <section className="mt-16" data-testid="festivals-section">
        <SectionHeading eyebrow="Muhurat" title="Upcoming Festivals & Vrat"
          subtitle="Sacred festivals and vrats ahead — with their tithi, auspicious timings and significance." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {festivals.map((f) => <MuhuratCard key={f.id} item={f} kind="festival" />)}
        </div>
      </section>

      <section className="mt-16" data-testid="rituals-section">
        <SectionHeading eyebrow="Muhurat" title="Upcoming Vedic Rituals"
          subtitle="Auspicious windows for life's important sanskars and havans — book a verified purohit for each." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rituals.map((r) => <MuhuratCard key={r.id} item={r} kind="ritual" />)}
        </div>
      </section>

      {/* How to read the Vedic Panchang — blog section */}
      {panchangBlogs.length > 0 && (
        <section className="mt-16" data-testid="howto-panchang-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <SectionHeading eyebrow="Gyan" title="How to read the Vedic Panchang"
              subtitle="New to the Panchang? Learn what Tithi, Nakshatra, Yoga, Karana and Vaar mean and how to find your shubh muhurat." />
            {guide && (
              <Link to={`/blog/${guide.slug}`} data-testid="howto-read-guide-link" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all whitespace-nowrap">
                Read the full guide <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {panchangBlogs.map((b) => (
              <Link to={`/blog/${b.slug}`} key={b.id} data-testid={`howto-blog-${b.id}`} className="rounded-2xl bg-card border border-border overflow-hidden hover-lift hover:border-accent group">
                <div className="h-40 overflow-hidden"><img src={b.image} alt="" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" /></div>
                <div className="p-5">
                  <span className="text-xs text-accent font-medium flex items-center gap-1"><ScrollText className="h-3.5 w-3.5" /> {b.category}</span>
                  <h3 className="font-serif text-lg font-semibold mt-1.5 leading-tight">{b.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{b.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-3 group-hover:gap-2.5 transition-all"><BookOpen className="h-4 w-4" /> Read article</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/blog" data-testid="howto-all-blogs-link"><Button variant="outline" className="rounded-full border-accent text-foreground hover:bg-accent hover:text-accent-foreground">Visit the Vedic Blog <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
          </div>
        </section>
      )}

      <p className="text-xs text-muted-foreground text-center max-w-2xl mx-auto px-4 pt-12">
        Note: Panchang timings shown are curated, illustrative samples for demonstration. For personalised muhurat, book a consultation with our verified purohits.
      </p>

      <div className="mt-16 -mx-4 sm:-mx-6 lg:-mx-12">
        <StatsSection stats={stats} />
        <TestimonialsSection testimonials={testimonials} />
      </div>
    </div>
  );
}
